import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import usePageTitle from '../hooks/usePageTitle';
import { FaArrowLeft, FaShield, FaExclamationTriangle, FaUsers, FaLock, FaGavel, FaEnvelope } from 'react-icons/fa';
import Logo from './Logo';

const ChildSafety = () => {
  const navigate = useNavigate();
  usePageTitle('Child Safety & CSAE Compliance');
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.2,
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  const sections = [
    {
      icon: <FaUsers />,
      title: "Age Restrictions",
      content: [
        {
          subtitle: "Minimum Age Requirement",
          text: "The datenite app is strictly for users aged 18 and older. Users must verify their age during account creation."
        },
        {
          subtitle: "Age Verification Measures",
          text: "We employ verification tools to prevent access by individuals below the minimum age. False age representation will result in account suspension."
        }
      ]
    },
    {
      // icon: <FaShield />,
      title: "Protection of Children",
      content: [
        {
          subtitle: "Prohibited Content",
          text: "Content promoting harm to minors, explicit material, or any form of child exploitation is strictly forbidden and will result in immediate reporting to relevant authorities."
        },
        {
          subtitle: "User Reporting",
          text: "Users can report any content or behaviour they believe violates child safety standards. Reports will be addressed within 24 hours, with urgent cases prioritised immediately."
        },
        {
          subtitle: "Content Moderation",
          text: "Our app employs moderation to detect and remove inappropriate content or profiles promptly."
        }
      ]
    },
    {
      icon: <FaLock />,
      title: "Data Privacy for Underage Users",
      content: [
        {
          subtitle: "No Collection of Minor Data",
          text: "We do not knowingly collect, store, or process personal information from individuals under 18."
        },
        {
          subtitle: "Parental Requests",
          text: "If a minor's information is identified within our system, parents or guardians can request its removal by contacting hello@datenite.co.uk."
        }
      ]
    },
    {
      icon: <FaGavel />,
      title: "User Conduct and CSAE Compliance",
      content: [
        {
          subtitle: "Code of Conduct",
          text: "Users must comply with our guidelines, which prohibit harmful, abusive, or exploitative behaviour. Violations will result in account suspension or termination."
        },
        {
          subtitle: "Mandatory Reporting",
          text: "We cooperate fully with legal authorities in cases of suspected harm, abuse, or exploitation of children identified through our platform."
        }
      ]
    },
    {
      // icon: <FaShield />,
      title: "App Features and Safety Measures",
      content: [
        {
          subtitle: "In-App Safety Controls",
          text: "Users have access to tools that allow blocking, reporting, and managing interactions to ensure a safe environment."
        }
      ]
    },
    {
      icon: <FaUsers />,
      title: "Partner and Advertiser Responsibility",
      content: [
        {
          subtitle: "Child-Safe Partnerships",
          text: "All partner businesses and advertisers must comply with Google's Child Safety Standards and datenite's CSAE policies. Non-compliance will result in the termination of partnerships."
        },
        {
          subtitle: "Age-Appropriate Content",
          text: "Advertisements and promotions displayed within the app must be appropriate for users aged 18 and older."
        }
      ]
    },
    {
      // icon: <FaShield />,
      title: "Continuous Improvement",
      content: [
        {
          subtitle: "Ongoing Training",
          text: "Our staff, moderators, and developers receive regular training on child safety, data privacy, and content moderation."
        },
        {
          subtitle: "Technology Upgrades",
          text: "We actively look to develop new ways to strengthen age verification, content moderation, and user safety."
        },
        {
          subtitle: "Policy Updates",
          text: "This Child Safety Policy will be reviewed and updated regularly to ensure compliance with new regulations and industry standards."
        }
      ]
    },
    {
      icon: <FaExclamationTriangle />,
      title: "Breach of Standards",
      content: [
        {
          subtitle: "",
          text: "Any breach of the Child Safety and CSAE Compliance Standards will result in immediate action, which may include:"
        }
      ]
    }
  ];

  const breachActions = [
    "Suspension or permanent removal of offending accounts.",
    "Reporting to relevant authorities where required.",
    "Termination of business relationships with non-compliant partners or advertisers."
  ];

  return (
    <div className="min-h-screen relative overflow-hidden">
      <div className="relative z-10">
        {/* Header */}
        <motion.div
          className="container mx-auto px-4 py-8"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.div variants={itemVariants} className="flex items-center gap-4 mb-8">
            <motion.button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-romantic-pink hover:text-white transition-colors duration-300"
              whileHover={{ x: -5 }}
              whileTap={{ scale: 0.95 }}
            >
              <FaArrowLeft className="text-xl" />
              <span className="font-semibold">Back to Home</span>
            </motion.button>
          </motion.div>

          <motion.div variants={itemVariants} className="text-center mb-12">
          <div className='flex justify-center items-center gap-4 flex-col'>
            <div className='text-center lg:text-left'>
            <img src="/assets/moon-logo.png" alt="Logo" className="w-32 mx-auto lg:mx-0" />
            </div>
          <div className='text-center lg:text-left'>
            <img src="/assets/png-logo.png" alt="Logo" className="w-40 mx-auto lg:mx-0 pb-2" />
            </div>
            </div>
            {/* <Logo size="large" className="mb-6 justify-center" /> */}
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-4">
              Child Safety & CSAE Compliance
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              At datenite, we prioritise the safety, security, and well-being of all users, 
              including implementing robust measures to comply with Google's Child Safety Standards for mobile apps.
            </p>
          </motion.div>
        </motion.div>

        {/* Main Content */}
        <main className="container mx-auto px-4 pb-12">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="max-w-4xl mx-auto"
          >

            {/* Sections */}
            <div className="space-y-12">
              {sections.map((section, index) => (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  className="glass rounded-2xl p-8 relative overflow-hidden"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-romantic-purple/10 to-romantic-pink/10" />
                  <div className="relative z-10">
                    <div className="flex items-center gap-4 mb-6">
                      <div className="w-10 h-10 bg-gradient-to-br from-romantic-purple to-romantic-pink rounded-full flex items-center justify-center text-white">
                        {section.icon}
                      </div>
                      <h2 className="text-2xl font-bold text-white">{section.title}</h2>
                    </div>
                    
                    <div className="space-y-6">
                      {section.content.map((item, itemIndex) => (
                        <div key={itemIndex}>
                          {item.subtitle && (
                            <h3 className="text-lg font-semibold text-romantic-pink mb-2">
                              {item.subtitle}:
                            </h3>
                          )}
                          <p className="text-gray-300 leading-relaxed">
                            {item.text}
                          </p>
                        </div>
                      ))}
                      
                      {section.title === "Breach of Standards" && (
                        <ul className="space-y-2 mt-4">
                          {breachActions.map((action, actionIndex) => (
                            <li key={actionIndex} className="flex items-start gap-3 text-gray-300">
                              <div className="w-2 h-2 bg-romantic-pink rounded-full mt-2 flex-shrink-0" />
                              <span>{action}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Contact Section */}
            <motion.div
              variants={itemVariants}
              className="mt-16 text-center"
            >
              <motion.div
                className="glass rounded-2xl p-8 relative overflow-hidden"
                whileHover={{ scale: 1.02 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-romantic-purple/20 to-romantic-pink/20" />
                <div className="relative z-10">
                  <div className="flex items-center justify-center gap-3 mb-4">
                    <FaEnvelope className="text-romantic-pink text-2xl" />
                    <h3 className="text-2xl font-bold text-white">Report a Concern</h3>
                  </div>
                  <p className="text-gray-300 mb-6 leading-relaxed">
                    If you have concerns regarding child safety or wish to report a violation, 
                    please contact us directly at:
                  </p>
                  <motion.a
                    href="mailto:hello@datenite.co.uk"
                    className="inline-flex items-center gap-2 bg-gradient-to-r from-romantic-purple to-romantic-pink text-white px-8 py-4 rounded-full font-semibold text-lg"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <FaEnvelope />
                    hello@datenite.co.uk
                  </motion.a>
                </div>
              </motion.div>
            </motion.div>

            {/* Last Updated */}
            <motion.div variants={itemVariants} className="text-center mt-12">
              <p className="text-gray-400 text-sm">
                Last updated: September 2025
              </p>
            </motion.div>
          </motion.div>
        </main>
      </div>
    </div>
  );
};

export default ChildSafety;
